// Zero-dependency IndexedDB cache for large dashboard map datasets.
//
// Why IndexedDB (not localStorage / Vuex):
//  - The map payload is 50k-100k rows (~7-14MB). localStorage's ~5MB quota
//    throws QuotaExceededError at that size, and it is synchronous so parsing
//    14MB JSON blocks the main thread (UI jank) on every load.
//  - Vuex survives in-app route navigation but is lost on a browser refresh (F5).
//  - IndexedDB is async (no main-thread blocking), has a much larger quota, and
//    persists across refresh.
//
// Storage layout (PER FILTER COMBINATION + PER PAGE):
//   `${filterKey}::meta`   -> { ts, complete, pages, limit }
//   `${filterKey}::p00001` -> rows[]   (one fetched page; zero-padded so the
//   `${filterKey}::p00002` -> rows[]    keys sort in numeric/fetch order)
//
// Per-page keys mean each write is one small page (~0.3-1MB), never a rewrite of
// the growing full blob. The cache fills progressively as pages arrive, and an
// interrupted load can RESUME from meta.pages + 1 instead of restarting.
//
// All operations are best-effort: any failure resolves to a cache-miss / no-op
// and the caller falls back to the network. The cache must never break the app.

const DB_NAME = "dimitra-dashboard";
const STORE = "mapData";
const DB_VERSION = 1;
const MAX_PAGE = 99999; // upper bound for page-key ranges (100k rows / 1k = 100 pages)

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !("indexedDB" in window)) {
      reject(new Error("IndexedDB not supported"));
      return;
    }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

const metaKey = (filterKey) => `${filterKey}::meta`;
const pageKey = (filterKey, page) => `${filterKey}::p${String(page).padStart(5, "0")}`;
// Range covering every page key for a filter. `::meta` sorts before `::p` so it
// is naturally excluded — no non-ASCII sentinel needed.
const pageRange = (filterKey) =>
  IDBKeyRange.bound(pageKey(filterKey, 1), pageKey(filterKey, MAX_PAGE));

/**
 * Build a stable, compact cache key from the filter params that determine the
 * result set. Same filters => same key => cache hit.
 *
 * `scope` MUST identify the logged-in user (e.g. userId). Different users see
 * different farm sets for identical filters, so without it user B would read
 * user A's cached data on a shared browser (wrong data + privacy leak).
 */
export function buildMapCacheKey(filters, scope) {
  return JSON.stringify({
    u: scope == null ? null : String(scope),
    s: filters.sDate || null,
    e: filters.eDate || null,
    c: filters.cropType || null,
    co: filters.country || null,
    st: filters.state || null,
    // sort so [1,2] and [2,1] hit the same cache entry
    f: Array.isArray(filters.farmIds)
      ? [...filters.farmIds].sort()
      : filters.farmIds || null,
  });
}

/** Returns the meta record { ts, complete, pages, limit } or null. */
export async function getCachedMapMeta(filterKey) {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(metaKey(filterKey));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn("[mapCache] meta read failed:", e);
    return null;
  }
}

/**
 * Reads and concatenates every cached page for the filter, in fetch order.
 * Returns rows[] (possibly partial) or null on miss / error.
 */
export async function getCachedMapPages(filterKey) {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll(pageRange(filterKey));
      req.onsuccess = () => {
        const pages = req.result || [];
        if (!pages.length) {
          resolve(null);
          return;
        }
        resolve(pages.flat()); // already key-ordered by zero-padded page number
      };
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.warn("[mapCache] pages read failed:", e);
    return null;
  }
}

/** Writes a single page's rows. Small, cheap, fire-and-forget. */
export async function setCachedMapPage(filterKey, page, rows) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(rows, pageKey(filterKey, page));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[mapCache] page write failed:", e);
  }
}

/** Writes/updates the meta record (ts is stamped automatically). */
export async function setCachedMapMeta(filterKey, meta) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put({ ts: Date.now(), ...meta }, metaKey(filterKey));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[mapCache] meta write failed:", e);
  }
}

/** Deletes the meta + all pages for one filter (e.g. stale, or before a fresh load). */
export async function clearMapCacheForKey(filterKey) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      store.delete(metaKey(filterKey));
      store.delete(pageRange(filterKey));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[mapCache] clearForKey failed:", e);
  }
}

/** Clears the entire map cache (e.g. for a manual global "refresh" button). */
export async function clearMapCache() {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    console.warn("[mapCache] clear failed:", e);
  }
}
